// Tiny segmented pill that flips the map between three lenses on the
// same dataset:
//   markers     — discrete points, one per outbreak (default)
//   heatmap     — case-weighted density bloom
//   choropleth  — country fill keyed by total case count
// Glass surface to match the rest of the chrome.
import { Flame, Globe, MapPin } from 'lucide-react';
import type { ViewMode } from '../lib/url-state';

interface Props {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
  /** Inline mode: full-width buttons stacked vertically, no glass pill —
   *  for the sidebar "Map views" section. Default is the floating pill. */
  inline?: boolean;
}

export function ViewToggle({ value, onChange, inline }: Props) {
  return (
    <div
      className={
        inline
          ? 'flex flex-wrap gap-1'
          : 'surface-glass pointer-events-auto inline-flex items-center p-0.5'
      }
    >
      <Option
        active={value === 'markers'}
        onClick={() => onChange('markers')}
        label="Markers"
        icon={<MapPin size={12} strokeWidth={2.25} />}
        inline={inline}
      />
      <Option
        active={value === 'heatmap'}
        onClick={() => onChange('heatmap')}
        label="Heatmap"
        icon={<Flame size={12} strokeWidth={2.25} />}
        inline={inline}
      />
      <Option
        active={value === 'choropleth'}
        onClick={() => onChange('choropleth')}
        label="By country"
        icon={<Globe size={12} strokeWidth={2.25} />}
        inline={inline}
      />
    </div>
  );
}

function Option({
  active, onClick, label, icon, inline,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
  inline?: boolean;
}) {
  if (inline) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={
          'inline-flex h-7 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-[12px] font-medium transition-colors ' +
          (active
            ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
            : 'border-[var(--color-line)] bg-white text-[var(--color-text-2)] hover:border-[var(--color-line-strong)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-accent)]')
        }
      >
        <span className={active ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-3)]'}>{icon}</span>
        {label}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'inline-flex h-6 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium transition-colors ' +
        (active
          ? 'bg-[var(--color-accent)] text-white shadow-sm'
          : 'text-[var(--color-text-2)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]')
      }
    >
      <span className={active ? 'text-white' : 'text-[var(--color-text-3)]'}>{icon}</span>
      {label}
    </button>
  );
}
